import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import DeviceForm from '@modules/kitplayShopAccount/device/DeviceForm';
import { showErrorMessage } from '@services/notifyService';
import { Button, Flex } from 'antd';

import DeviceKitplayForm from './DeviceKitplayForm';

const message = defineMessages({
    updateDeviceSuccess: 'Update device successfully',
});

const MAIN_FORM_ID = 'form-kitplay-device-main';
const CHILD_FORM_ID = 'form-kitplay-device-child';

const useDeviceFormModal = ({ onReloadList, onReloadChild, onCloseMain, onCloseChild } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [ openMainModal, setOpenMainModal ] = useState(false);
    const [ mainDetail, setMainDetail ] = useState(null);
    const [ mainChanged, setMainChanged ] = useState(false);

    const [ openChildModal, setOpenChildModal ] = useState(false);
    const [ childDetail, setChildDetail ] = useState(null);
    const [ childChanged, setChildChanged ] = useState(false);
    const [ childParent, setChildParent ] = useState(null);
    const [ childDeviceId, setChildDeviceId ] = useState(null);

    const { execute: executeGetDetail } = useFetch(apiConfig.kitplayDevice.getById, { immediate: false });
    const { execute: executeUpdateMain, loading: loadingMain } = useFetch(apiConfig.kitplayDevice.update, {
        immediate: false,
    });
    const { execute: executeUpdateChild, loading: loadingChild } = useFetch(apiConfig.printer.update, {
        immediate: false,
    });

    const mappingDetail = (res) => (res?.result === true ? res.data : {});

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeMainModal = () => {
        setOpenMainModal(false);
        setMainDetail(null);
        setMainChanged(false);
        onCloseMain?.();
    };

    const openMainEdit = (record) => {
        executeGetDetail({
            pathParams: { id: record.id },
            onCompleted: (res) => {
                setMainDetail(mappingDetail(res));
                setMainChanged(false);
                setOpenMainModal(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const handleSaveMain = (values) => {
        executeUpdateMain({
            data: { ...values, id: mainDetail?.id },
            onCompleted: (res) => {
                if (res?.result === true) {
                    notification({ message: translate.formatMessage(message.updateDeviceSuccess) });
                    closeMainModal();
                    onReloadList?.();
                }
            },
            onError: handleSaveError,
        });
    };

    const closeChildModal = () => {
        setOpenChildModal(false);
        setChildDetail(null);
        setChildParent(null);
        setChildDeviceId(null);
        setChildChanged(false);
        onCloseChild?.();
    };

    const openChildEdit = (record) => {
        executeGetDetail({
            pathParams: { id: record.id },
            onCompleted: (res) => {
                setChildDetail(mappingDetail(res));
                setChildParent(record.parent);
                setChildDeviceId(record?.parent?.id);
                setChildChanged(false);
                setOpenChildModal(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const handleSaveChild = (values) => {
        executeUpdateChild({
            data: { ...values, id: childDetail?.id },
            onCompleted: (res) => {
                if (res?.result === true) {
                    notification({ message: translate.formatMessage(message.updateDeviceSuccess) });
                    if (childParent) onReloadChild?.(childParent.id);
                    closeChildModal();
                    onReloadList?.();
                }
            },
            onError: handleSaveError,
        });
    };

    const renderModalFooter = (formId, onCancel, loading, disabled) => (
        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
            <Button danger onClick={onCancel} icon={<StopOutlined />}>
                {translate.formatMessage(commonMessage.cancel)}
            </Button>
            <Button
                type="primary"
                htmlType="submit"
                form={formId}
                loading={loading}
                disabled={disabled}
                icon={<SaveOutlined />}
            >
                {translate.formatMessage(commonMessage.update)}
            </Button>
        </Flex>
    );

    const renderModals = () => (
        <>
            {openMainModal && (
                <BaseModal
                    isModalVisible={openMainModal}
                    isEditing={true}
                    handleCancel={closeMainModal}
                    title={translate.formatMessage(commonMessage.managerKitplay)}
                    customFooter={renderModalFooter(MAIN_FORM_ID, closeMainModal, loadingMain, !mainChanged)}
                    width="800px"
                    noBodyPadding={true}
                >
                    <DeviceForm
                        formId={MAIN_FORM_ID}
                        dataDetail={mainDetail || {}}
                        isEditing={true}
                        actions={null}
                        onSubmit={handleSaveMain}
                        setIsChangedFormValues={setMainChanged}
                    />
                </BaseModal>
            )}

            {openChildModal && (
                <BaseModal
                    isModalVisible={openChildModal}
                    isEditing={true}
                    handleCancel={closeChildModal}
                    title={translate.formatMessage(commonMessage.managerKitplay)}
                    customFooter={renderModalFooter(CHILD_FORM_ID, closeChildModal, loadingChild, !childChanged)}
                    width="800px"
                    noBodyPadding={true}
                >
                    <DeviceKitplayForm
                        formId={CHILD_FORM_ID}
                        dataDetail={childDetail || {}}
                        isEditing={true}
                        deviceId={childDeviceId}
                        actions={null}
                        onSubmit={handleSaveChild}
                        setIsChangedFormValues={setChildChanged}
                    />
                </BaseModal>
            )}
        </>
    );

    return { openMainEdit, openChildEdit, renderModals };
};

export default useDeviceFormModal;
